import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Shared hero used by Safety / About / Contact pages.
 * Keeps the brand language (eyebrow, gradient-accented headline) consistent.
 */
export default function PageHero({
  eyebrow,
  title,
  accent,
  description,
  primaryCta,
  secondaryCta,
  image,
  badge,
}) {
  return (
    <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-grid mask-radial" />
      <div className="absolute -top-40 -right-40 -z-10 h-[520px] w-[520px] rounded-full bg-[#00AEEF]/15 blur-3xl" />
      <div className="absolute -top-20 -left-32 -z-10 h-[420px] w-[420px] rounded-full bg-[#0070C0]/15 blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className={`grid ${image ? "lg:grid-cols-12 gap-10 lg:gap-14 items-center" : "max-w-3xl"}`}>
          <div className={image ? "lg:col-span-7" : ""}>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0070C0] shadow-sm"
            >
              {badge ? badge : eyebrow}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display mt-6 text-[40px] sm:text-5xl lg:text-[64px] font-extrabold leading-[1.04] tracking-tighter text-[#002050]"
            >
              {title}{" "}
              {accent && (
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-[#0070C0] via-[#00AEEF] to-[#0070C0] bg-clip-text text-transparent">
                    {accent}
                  </span>
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 300 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 8 C 80 2, 200 2, 298 8"
                      stroke="#00AEEF"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              )}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 max-w-2xl text-lg md:text-xl text-[#1F2937]/80 leading-relaxed"
            >
              {description}
            </motion.p>

            {(primaryCta || secondaryCta) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                {primaryCta && (
                  <Link
                    to={primaryCta.to}
                    data-testid={primaryCta.testId}
                    className="group inline-flex items-center gap-2 bg-[#0070C0] hover:bg-[#002050] text-white px-7 py-3.5 rounded-xl text-[15px] font-bold transition-all duration-300 shadow-[0_8px_24px_rgba(0,112,192,0.35)] hover:shadow-[0_12px_30px_rgba(0,32,80,0.35)] hover:-translate-y-0.5"
                  >
                    {primaryCta.label}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
                {secondaryCta && (
                  <Link
                    to={secondaryCta.to}
                    data-testid={secondaryCta.testId}
                    className="inline-flex items-center gap-2 bg-white text-[#002050] border-2 border-[#E2E8F0] hover:border-[#0070C0] px-7 py-3.5 rounded-xl text-[15px] font-bold transition-all duration-300"
                  >
                    {secondaryCta.label}
                  </Link>
                )}
              </motion.div>
            )}
          </div>

          {image && (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,32,80,0.35)] border border-white/40">
                <img
                  src={image}
                  alt=""
                  className="w-full h-[360px] md:h-[460px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#002050]/55 via-[#002050]/10 to-transparent" />
              </div>
              <div className="absolute -z-10 -bottom-10 -right-10 h-60 w-60 rounded-full bg-[#00AEEF]/30 blur-3xl" />
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
